import { useLocation } from 'react-router';

import { RoccoLink } from '@rocco/components/link';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { professionalTabBase } from '@modules/professional/util/professional-tab-base';
import { IN_PAGE_SEARCH_PARAM_KEY } from '@modules/professional/config/constants';
import { usePageCtas } from '~/modules/enquiry/component/PageCtasProvider';
import {
    useBookCta,
    useEnquireCta,
} from '~/modules/enquiry/hooks/use-page-ctas';
import { useClickToReveal } from '~/modules/root/hook/use-click-to-reveal';
import {
    buildLocationRevealExtraData,
    sortLocationsByMemberOrder,
} from '@modules/professional/util/contact-page-helper';
import {
    DetailCreditsRow,
    type DetailCreditPair,
} from '~/ui-rocco/pages/detail-shared';
import { buildProfessionalTrustSignals } from '../utils/professional-trust-signals';
import { BUSINESS_FOCUS_TEXT } from './ProfessionalSpecList';
import { SpecifiedByList } from '~/ui-rocco/pages/detail-shared/SpecifiedByList';

/** The row's 12px uppercase names, as a control. */
const ACTION_CLASS =
    'cursor-pointer bg-transparent p-0 text-left font-sans text-[0.75rem] leading-normal text-[#181818] uppercase no-underline transition-opacity hover:no-underline hover:opacity-70';

/** Past this many lines of tags, the rest wait behind a "+N More" pill. */
const MAX_TAG_LINES = 5;

const BOOK_A_MEETING = 'Book a Meeting';

const HOURS_PER_DAY = 24;
/**
 * Past two days a response time reads as a warning rather than a reason to get
 * in touch, so it is left off, as the practice card on project pages leaves it.
 */
const MAX_SHOWN_RESPONSE_HOURS = 48;

/**
 * "Within 3 hours", "Within 2 days" — or nothing when the practice has too few
 * enquiries to measure (the field is 0) or answers slower than two days.
 */
const responseTimeText = (hours: number | null | undefined) => {
    if (!hours || hours <= 0 || hours > MAX_SHOWN_RESPONSE_HOURS) {
        return undefined;
    }
    if (hours < HOURS_PER_DAY) {
        return `Within ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }
    const days = Math.round(hours / HOURS_PER_DAY);
    return `Within ${days} ${days === 1 ? 'day' : 'days'}`;
};
const ENQUIRE_NOW = 'Enquire Now';

/**
 * The phone the practice publishes, if any — the tracking number where one is
 * set, as the reveal shows it.
 */
const publishedPhone = (professional: {
    PhoneTracking?: string | null;
    Phone?: string | null;
}) => professional.PhoneTracking || professional.Phone || undefined;

/** Locations the practice shows on its Contact tab. */
const shownLocations = <
    T extends { LocationHidden?: boolean | null },
>(professional: {
    LibraryLocations?: readonly T[] | null;
}) =>
    (professional.LibraryLocations ?? []).filter(
        (location) => !location.LocationHidden,
    );

/**
 * The ways to reach the practice, as a column of the meta row: a meeting and an
 * enquiry through the same flows as the page's CTAs, the phone number behind
 * the same reveal as the spec list, and the practice's locations on the Contact
 * tab. The enquiry channels respect the practice's enquiry audience.
 */
export const GetInTouchLinks = () => {
    const initial = useProfessionalInitialRouteData();
    const { pathname } = useLocation();
    const enquire = useEnquireCta({
        source: 'ProfessionalOverview',
        fixedLabel: ENQUIRE_NOW,
    });
    // With the booking gate off, a meeting request goes through the enquiry
    // modal, as every other Book a Meeting entry point does.
    const book = useBookCta({
        source: 'ProfessionalOverview',
        fixedLabel: BOOK_A_MEETING,
    });
    const { revealed, onRevealClick } = useClickToReveal({
        revealType: 'Phone',
        revealKey: `professional-${initial.ID}`,
        professionalId: initial.ID,
    });

    const phone = publishedPhone(initial);
    const locations = shownLocations(initial);
    const locationCount = locations.length;
    // The Contact tab opens on the primary location, so the reveal names it,
    // as the legacy Overview's location link did.
    const primaryLocation = sortLocationsByMemberOrder(locations)[0];
    const { onRevealClick: onLocationReveal } = useClickToReveal({
        revealType: 'Location',
        revealKey: `professional-${initial.ID}`,
        revealSubKey: (primaryLocation?.ID ?? 0).toString(),
        professionalId: initial.ID,
        extraData: buildLocationRevealExtraData(primaryLocation),
    });

    return (
        <ul className="m-0 flex list-none flex-col gap-0 p-0">
            {book.visible ? (
                <li>
                    <button
                        type="button"
                        onClick={book.open}
                        className={ACTION_CLASS}
                    >
                        {book.label}
                    </button>
                </li>
            ) : null}
            {enquire.visible ? (
                <li>
                    <button
                        type="button"
                        onClick={() => enquire.open()}
                        className={ACTION_CLASS}
                    >
                        {enquire.label}
                    </button>
                </li>
            ) : null}
            {phone ? (
                <li>
                    {revealed ? (
                        <a
                            href={`tel:${phone.replace(/[^+\d]/g, '')}`}
                            className={ACTION_CLASS}
                        >
                            {phone}
                        </a>
                    ) : (
                        <button
                            type="button"
                            onClick={onRevealClick}
                            className={ACTION_CLASS}
                        >
                            View Phone number
                        </button>
                    )}
                </li>
            ) : null}
            {locationCount > 0 ? (
                <li>
                    <RoccoLink
                        to={`${professionalTabBase(initial.URLSegment, pathname)}/contact`}
                        onClick={onLocationReveal}
                        className={ACTION_CLASS}
                    >
                        {locationCount === 1
                            ? 'View location'
                            : 'View locations'}
                    </RoccoLink>
                </li>
            ) : null}
        </ul>
    );
};

/**
 * The practice's business details — category, response time, focus, member
 * since, awards, then the bodies it belongs to — and whether it offers any way
 * to get in touch. Shared by the desktop meta row and the phone's About tab.
 */
export const useProfessionalBusinessDetails = (): {
    details: DetailCreditPair[];
    hasGetInTouch: boolean;
} | null => {
    const initial = useProfessionalInitialRouteData();
    // Whether there is any way to get in touch decides whether its block
    // exists at all: a heading over nothing is what both layouts avoid.
    const { ctas } = usePageCtas();
    if (!initial) return null;

    // Deduped on name, keeping the first segment seen: an association is a
    // professional in its own right, so its name points at its profile.
    const associations = Array.from(
        (initial.Associations ?? [])
            .reduce((byName, association) => {
                const name = association.Title?.trim();
                if (!name || byName.has(name)) return byName;
                byName.set(name, {
                    name,
                    link: association.URLSegment
                        ? `/professional/${association.URLSegment}`
                        : undefined,
                });
                return byName;
            }, new Map<string, { name: string; link?: string }>())
            .values(),
    );

    // Only what the profile actually carries: each row drops out without a
    // value, and the column with them.
    const { badges } = buildProfessionalTrustSignals(initial);
    const memberSince = badges
        .find((badge) => badge.id === 'verified')
        ?.detail?.replace(/^Since\s+/, '');
    const awardCount = initial.Awards?.edges?.length ?? 0;
    const businessFocus = initial.BusinessFocus
        ? BUSINESS_FOCUS_TEXT[initial.BusinessFocus]
        : undefined;
    const businessDetails = [
        {
            label: 'Category:',
            value: initial.PrimaryCategory?.PrettyTitle?.trim() || undefined,
        },
        {
            label: 'Average response time:',
            value: responseTimeText(initial.EnquiryAvgResponseHours),
        },
        { label: 'Business focus:', value: businessFocus },
        { label: 'Verified ArchiPro member since:', value: memberSince },
        {
            label: 'Award winning professional:',
            value:
                awardCount > 0
                    ? `${awardCount} ${awardCount === 1 ? 'award' : 'awards'}`
                    : undefined,
        },
    ].filter((pair): pair is { label: string; value: string } =>
        Boolean(pair.value),
    );
    // The bodies it belongs to, last, their names under the label.
    const details: DetailCreditPair[] =
        associations.length > 0
            ? [
                  ...businessDetails,
                  {
                      label: 'Associations:',
                      value: associations.map(({ name }) => name).join(', '),
                      valuesBelow: associations.map(({ name, link }) => ({
                          value: name,
                          link,
                      })),
                  },
              ]
            : businessDetails;

    // Nothing to offer means no column: with enquiries closed, no published
    // phone and every location hidden, the list comes out empty and the label
    // would stand over blank space.
    const hasGetInTouch =
        ctas.enquire.visible ||
        ctas.book.visible ||
        !!publishedPhone(initial) ||
        shownLocations(initial).length > 0;

    return { details, hasGetInTouch };
};

/**
 * A tag searches the tab that holds the work it names: a supplier's products, or
 * every other practice's projects.
 */
const tagLink = (tabBase: string, isSupplier: boolean, tag: string) => {
    const tab = isSupplier ? 'our-products' : 'case-studies';
    const query = new URLSearchParams({ [IN_PAGE_SEARCH_PARAM_KEY]: tag });
    return `${tabBase}/${tab}?${query.toString()}`;
};

/**
 * The product page's meta row, on the profile: the practice's services as tags
 * that search its projects or products (five lines, then a "+N More"), how to
 * get in touch, its business details ending with the bodies it belongs to,
 * and, for a supplier, the professionals who specify its products. Each
 * part hides when empty.
 */
export const ProfessionalMetaRow = () => {
    const initial = useProfessionalInitialRouteData();
    const { pathname } = useLocation();
    const businessDetails = useProfessionalBusinessDetails();
    if (!initial || !businessDetails) return null;

    const tabBase = professionalTabBase(initial.URLSegment, pathname);
    const services = Array.from(
        new Set(
            (initial.Services ?? [])
                .map((service) => service.trim())
                .filter((service) => service !== ''),
        ),
    ).map((label) => ({
        label,
        to: tagLink(tabBase, Boolean(initial.IsManufacturer), label),
    }));

    const { details, hasGetInTouch } = businessDetails;

    // A supplier's specifiers: the professionals who use its products.
    const specifiers = initial.IsManufacturer ? initial.SpecifiedBy : [];

    return (
        <DetailCreditsRow
            tags={services}
            maxTagLines={MAX_TAG_LINES}
            columns={[
                {
                    label: 'Get in touch',
                    entries: [],
                    node: hasGetInTouch ? <GetInTouchLinks /> : undefined,
                },
                {
                    label: 'Business details',
                    entries: [],
                    pairs: details,
                    pairsOnOneLine: true,
                },
                {
                    label: 'Specified by',
                    entries: [],
                    node:
                        specifiers.length > 0 ? (
                            <SpecifiedByList
                                specifiers={specifiers}
                                moreTo={`${tabBase}/partners`}
                            />
                        ) : undefined,
                },
            ]}
        />
    );
};
